import React, { memo, useEffect } from "react";
import DatePicker from "@components/datePicker/DatePicker";
import RadioBottom from "@components/radioBottom/RadioBottom";
import CheckBox from "@components/checkBox/CheckBox";
import dayjs from "dayjs";
import { AcademicTermType } from "@store/AcademicTerms";
import { HolidaysType } from "@store/Holiday";
import { getDateFormatted } from "@utils/format";

interface ScheduleStudentProps {
  formik: any;
  academicTermsList: AcademicTermType[];
  holidays: HolidaysType;
  registrationDate: AcademicYearType | null;
  fetching?: boolean;
}

const ScheduleStudent = ({
  formik,
  academicTermsList,
  holidays,
  registrationDate,
  fetching,
}: ScheduleStudentProps) => {
  const { listHolidays } = holidays;

  const SetIncludeDates = () => {
    const listIncludeDates: Date[] = [];

    if (!academicTermsList) {
      return;
    }

    if (academicTermsList.length < 0) {
      return;
    }

    academicTermsList.forEach((academicTerm) => {
      let start;

      if (academicTerm.hasOwnProperty("endDate")) {
        if (
          academicTerm.startDate.slice(0, 10) >=
          new Date().toISOString().slice(0, 10)
        ) {
          start = academicTerm.startDate;
        } else {
          start = getDateFormatted(new Date());
        }

        const startDate = start;
        const endDate = academicTerm.endDate.slice(0, 10);
        const dateMove = new Date(startDate);
        let strDate = startDate;

        while (strDate < endDate) {
          strDate = getDateFormatted(dateMove);
          listIncludeDates.push(new Date(strDate));
          dateMove.setDate(dateMove.getDate() + 1);
        }
      } else {
        listIncludeDates.push(new Date(academicTerm.startDate));
      }
    });

    return listIncludeDates;
  };

  const SetExcludeDates = () => {
    const listExcludeDates: any = [];

    if (!listHolidays) {
      return;
    }

    if (listHolidays.length < 0) {
      return;
    }

    listHolidays.forEach((holiday) => {
      if (holiday.date) {
        listExcludeDates.push(new Date(holiday.date));
      }
    });

    return listExcludeDates;
  };

  const isWeekday = (date: Date) => {
    const day = date.getDay();
    console.log(day, date);
    return day !== 0 && day !== 6;
  };

  const validateDay = (selectDay: boolean) => {
    if (selectDay) {
      formik.setFieldValue("daySelect", formik.values.daySelect - 1);
    } else {
      formik.setFieldValue("daySelect", formik.values.daySelect + 1);
    }
  };

  const validateSchedulte = () => {
    if (registrationDate === null) {
      return null;
    }

    let now = new Date();

    // check if date.time Now inside of early Registration Dates
    // if true, will use startDate academic year as minimum date value
    // else, date.time Now will add 14 days.
    if (
      now.getTime() >=
        new Date(registrationDate.earlyRegistrationStartDate).getTime() &&
      now.getTime() <=
        new Date(registrationDate.earlyRegistrationEndDate).getTime()
    ) {
      return registrationDate.startDate;
    }

    now.setDate(now.getDate() + 14);
    let dateNowPlusFourteen = now.getTime();

    // if date.time Now + 14 days before lateRegistrationStartDate
    // will use lateRegistrationStartDate as minimum date value
    // else will use date.time Now + 14 days as minimum date value
    if (
      dateNowPlusFourteen <
      new Date(registrationDate.lateRegistrationStartDate).getTime()
    ) {
      return registrationDate.lateRegistrationStartDate;
    }

    return new Date(dateNowPlusFourteen).toISOString().slice(0, 10);
  };

  const disabledInput = () => {
    if (registrationDate === null) {
      return true;
    }

    let now = new Date();
    now.setDate(now.getDate() + 11);

    // input will disable when date.time Now + 11 days not in early Registration Date
    if (
      new Date(registrationDate.earlyRegistrationStartDate).getTime() >
      now.getTime()
    ) {
      return true;
    }

    return false;
  };

  return (
    <>
      <div className="row g-0">
        <h1 className="pl-3 pr-3 formElements__inputSectionHeader header--18">
          Schedule
        </h1>
      </div>
      <DatePicker
        title="Start Date *"
        onChange={(date) => {
          formik.setFieldValue("startDate", dayjs(date).format("YYYY-MM-DD"));
        }}
        selected={
          formik.values.startDate
            ? dayjs(formik.values.startDate).toDate()
            : null
        }
        includeDates={SetIncludeDates()}
        excludeDates={SetExcludeDates()}
        mobileScreen={true}
        minDate={new Date(validateSchedulte() || "")}
        maxDate={new Date(registrationDate?.endDate || "")}
        filterDate={isWeekday}
        disabled={disabledInput()}
        isLoading={fetching}
      ></DatePicker>

      <div className="js-multiInputWrapper js-isRequired w-100 formElements__group">
        <label className="formElements__label">To and/or From *</label>
        <div className="row my-4">
          <div className="col-sm-4">
            <RadioBottom
              name="schedule"
              value="morning"
              label="Morning (Bus to School)"
              checked={formik.values.toAndFrom === "morning"}
              onChange={() => {
                formik.setFieldValue("toAndFrom", "morning");
              }}
            ></RadioBottom>
          </div>
          <div className="col-sm-4">
            <RadioBottom
              name="schedule"
              value="afternoon"
              label="Afternoon (Bus from School)"
              checked={formik.values.toAndFrom === "afternoon"}
              onChange={() => {
                formik.setFieldValue("toAndFrom", "afternoon");
              }}
            ></RadioBottom>
          </div>
          <div className="col-sm-4">
            <RadioBottom
              name="schedule"
              value="both"
              label="Both"
              checked={formik.values.toAndFrom === "both"}
              onChange={() => {
                formik.setFieldValue("toAndFrom", "both");
              }}
            ></RadioBottom>
          </div>
          {formik.touched.toAndFrom && formik.errors.toAndFrom ? (
            <div className="formElements__errorText">
              {formik.errors.toAndFrom}
            </div>
          ) : null}
        </div>
        <label className="formElements__label">Day *</label>
        <div className="row">
          <div className="col-sm-4">
            <CheckBox
              label="Monday"
              value="Monday"
              checked={formik.values.isMonday}
              onChange={() => {
                formik.setFieldValue("isMonday", !formik.values.isMonday);
                validateDay(formik.values.isMonday);
              }}
            ></CheckBox>
          </div>
          <div className="col-sm-4">
            <CheckBox
              label="Tuesday"
              value="Tuesday"
              checked={formik.values.isTuesday}
              onChange={() => {
                formik.setFieldValue("isTuesday", !formik.values.isTuesday);
                validateDay(formik.values.isTuesday);
              }}
            ></CheckBox>
          </div>
          <div className="col-sm-4">
            <CheckBox
              label="Wednesday"
              value="Wednesday"
              checked={formik.values.isWednesday}
              onChange={() => {
                formik.setFieldValue("isWednesday", !formik.values.isWednesday);
                validateDay(formik.values.isWednesday);
              }}
            ></CheckBox>
          </div>
          <div className="col-sm-4">
            <CheckBox
              label="Thursday"
              value="Thursday"
              checked={formik.values.isThursday}
              onChange={() => {
                formik.setFieldValue("isThursday", !formik.values.isThursday);
                validateDay(formik.values.isThursday);
              }}
            ></CheckBox>
          </div>
          <div className="col-sm-4">
            <CheckBox
              label="Friday"
              value="Friday"
              checked={formik.values.isFriday}
              onChange={() => {
                formik.setFieldValue("isFriday", !formik.values.isFriday);
                validateDay(formik.values.isFriday);
              }}
            ></CheckBox>
          </div>

          {formik.touched.daySelect && formik.errors.daySelect ? (
            <div className="formElements__errorText">
              {formik.errors.daySelect}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default ScheduleStudent;
